import { FILTER_CONSTANTS } from '@/constants/FilterConstants';

/**
 * Utility functions for working with count maps and filter data
 */
export class CountMapUtils {
  /**
   * Gets the total count from a count map
   */
  static getTotalCount(countMap: Map<string | null, number>): number {
    return countMap.get(FILTER_CONSTANTS.TOTAL_COUNT_KEY) || 0;
  }

  /**
   * Sets the total count in a count map
   */
  static setTotalCount(
    countMap: Map<string | null, number>,
    count: number
  ): void {
    countMap.set(FILTER_CONSTANTS.TOTAL_COUNT_KEY, count);
  }

  /**
   * Filters out the total count key from count map entries
   */
  static filterOutTotalCount<T>(
    entries: Array<[string | null, T]>
  ): Array<[string | null, T]> {
    return entries.filter(([key]) => key !== FILTER_CONSTANTS.TOTAL_COUNT_KEY);
  }

  /**
   * Checks if a key is the total count key
   */
  static isTotalCountKey(key: string | null): boolean {
    return key === FILTER_CONSTANTS.TOTAL_COUNT_KEY;
  }

  /**
   * Creates a count map with just the total count
   */
  static createTotalCountMap(totalCount: number): Map<string | null, number> {
    return new Map([[FILTER_CONSTANTS.TOTAL_COUNT_KEY, totalCount]]);
  }
}

/**
 * Utility functions specifically for ML Inactive Flags
 */
export class MLInactiveFlagsUtils {
  /**
   * Filters out flags that represent the total count
   */
  static filterOutCountFlags<T extends { key: string }>(flags: T[]): T[] {
    return flags.filter(flag => !CountMapUtils.isTotalCountKey(flag.key));
  }

  /**
   * Gets the enabled flags excluding the count flag
   */
  static getEnabledFlags<T extends { key: string; isDefaultEnabled: boolean }>(
    flags: T[]
  ): T[] {
    return flags.filter(
      flag => flag.isDefaultEnabled && !CountMapUtils.isTotalCountKey(flag.key)
    );
  }

  /**
   * Finds the total count flag in a flags array
   */
  static findTotalCountFlag<T extends { key: string }>(
    flags: T[]
  ): T | undefined {
    return flags.find(flag => CountMapUtils.isTotalCountKey(flag.key));
  }
}
